/**
 * The scenario, said out loud in one sentence (PRD Part E §1).
 *
 * Always visible on the overview and the run views, assembled from bound state,
 * every clause linking to where it is changed. Presentation only — every field
 * it reads already existed; what was missing was anybody saying them together.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import { usePlanningEngine } from './PlanningEngineContext';
import { engineInputKindLabel } from '../../../lib/planningEngineService';
import { listPlanCycles } from '../../../lib/planningService';
import {
  buildScenarioSummaryClauses,
  scenarioSummarySentence,
  type ScenarioSummaryClause,
} from '../../../lib/planningScenarioSummary';

function ClauseText({ clause }: { clause: ScenarioSummaryClause }) {
  const value = (
    <span className="font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{clause.value}</span>
  );
  return (
    <span title={clause.title}>
      {clause.lead}{' '}
      {clause.href ? (
        <Link
          to={clause.href}
          className="underline decoration-dotted underline-offset-2 hover:decoration-solid"
        >
          {value}
        </Link>
      ) : (
        value
      )}
      {clause.trail ? ` ${clause.trail}` : ''}
    </span>
  );
}

export default function ScenarioSummarySentence({
  basePath = '/planning/engine',
}: {
  basePath?: string;
}) {
  const { activeScenario, engineInput, projectId } = usePlanningEngine();
  const [planCycleName, setPlanCycleName] = useState<string | null>(null);

  const planCycleId = activeScenario?.planCycleId ?? null;
  useEffect(() => {
    if (!planCycleId || !projectId) {
      setPlanCycleName(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await listPlanCycles({ workspaceId: projectId });
        if (cancelled) return;
        setPlanCycleName(data.find((c) => c.id === planCycleId)?.name ?? null);
      } catch {
        if (!cancelled) setPlanCycleName(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [planCycleId, projectId]);

  if (!activeScenario) return null;

  const scopeLabel =
    engineInput.type === 'live'
      ? 'All Live Items'
      : `${engineInputKindLabel(engineInput.type)}: ${engineInput.name}`;

  const clauses = buildScenarioSummaryClauses({
    scenario: activeScenario,
    scopeLabel,
    planCycleName,
    basePath,
  });

  return (
    <div
      className="flex items-start gap-2.5 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg-raised)] px-4 py-3"
      title={scenarioSummarySentence(clauses)}
    >
      <Info className="h-4 w-4 mt-0.5 shrink-0 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
      <p className="text-sm leading-relaxed text-[var(--text-sec)] dark:text-[var(--text-sec)]">
        {clauses.map((clause, i) => (
          <span key={clause.key}>
            {i > 0 && <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]"> · </span>}
            <ClauseText clause={clause} />
          </span>
        ))}
        .
      </p>
    </div>
  );
}
